import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Select, message, Space, 
  Tree, Card, Row, Col, Popconfirm, InputNumber, Switch
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, ArrowRightOutlined,
  FileOutlined, SettingOutlined, TeamOutlined, KeyOutlined, 
  HomeOutlined, MenuOutlined
} from '@ant-design/icons';
import { menuAPI, adminAPI } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMenus, setUserMenus] = useState([]);
  const [availableMenus, setAvailableMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // 图标映射
  const iconMap = {
    'FileOutlined': <FileOutlined />,
    'SettingOutlined': <SettingOutlined />,
    'TeamOutlined': <TeamOutlined />,
    'KeyOutlined': <KeyOutlined />,
    'HomeOutlined': <HomeOutlined />,
    'MenuOutlined': <MenuOutlined />,
  };

  const renderIcon = (iconName) => {
    return iconMap[iconName] || <FileOutlined />;
  };

  useEffect(() => {
    fetchMenus();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserMenus(selectedUser);
      fetchAvailableMenus(selectedUser);
    }
  }, [selectedUser]);

  const fetchMenus = async () => {
    try {
      const response = await menuAPI.listMenusFlat();
      const menusData = response.data || response;
      setMenus(menusData);
    } catch (error) {
      message.error('获取菜单列表失败');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.listUsers();
      const usersData = response.data || response;
      setUsers(usersData);
      if (usersData.length > 0) {
        setSelectedUser(usersData[0].id);
      }
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  const fetchUserMenus = async (userId) => {
    try {
      const response = await menuAPI.getUserMenus(userId);
      const userMenusData = response.data || response;
      setUserMenus(userMenusData);
    } catch (error) {
      message.error('获取用户菜单失败');
    }
  };

  const fetchAvailableMenus = async (userId) => {
    try {
      const response = await menuAPI.getAvailableMenus(userId);
      const availableMenusData = response.data || response;
      setAvailableMenus(availableMenusData);
    } catch (error) {
      message.error('获取可分配菜单失败');
      console.error('获取可分配菜单错误详情:', error);
    }
  };

  const handleCreateMenu = async (values) => {
    setLoading(true);
    try {
      await menuAPI.createMenu(values);
      message.success('菜单创建成功');
      setCreateModalVisible(false);
      form.resetFields();
      fetchMenus();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMenu = async (values) => {
    setLoading(true);
    try {
      await menuAPI.updateMenu(currentMenu.id, values);
      message.success('菜单更新成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setCurrentMenu(null);
      fetchMenus();
    } catch (error) {
      message.error(error.response?.data?.detail || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMenu = async (menuId) => {
    try {
      await menuAPI.deleteMenu(menuId);
      message.success('菜单删除成功');
      fetchMenus();
    } catch (error) {
      message.error(error.response?.data?.detail || '删除失败');
    }
  };

  const handleAssignMenu = async (menuId) => {
    if (!selectedUser) return;
    
    try {
      await menuAPI.assignMenuToUser(selectedUser, menuId);
      message.success('菜单分配成功');
      fetchUserMenus(selectedUser);
      fetchAvailableMenus(selectedUser);
    } catch (error) {
      message.error(error.response?.data?.detail || '分配失败');
    }
  };

  const handleRemoveMenu = async (menuId) => {
    if (!selectedUser) return;
    
    try {
      await menuAPI.removeMenuFromUser(selectedUser, menuId);
      message.success('菜单移除成功');
      fetchUserMenus(selectedUser);
      fetchAvailableMenus(selectedUser);
    } catch (error) {
      message.error(error.response?.data?.detail || '移除失败');
    }
  };

  const openEditModal = (menu) => {
    setCurrentMenu(menu);
    editForm.setFieldsValue({
      name: menu.name,
      icon: menu.icon,
      path: menu.path,
      parent_id: menu.parent_id,
      file_path: menu.file_path,
      menu_type: menu.menu_type,
      sort_order: menu.sort_order,
      enabled: menu.enabled,
    });
    setEditModalVisible(true);
  };

  const menuColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '菜单名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 150,
      render: (icon) => icon ? renderIcon(icon) : <FileOutlined />,
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
      width: 200,
    },
    {
      title: '文件路径',
      dataIndex: 'file_path',
      key: 'file_path',
      width: 300,
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'menu_type',
      key: 'menu_type',
      width: 100,
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled) => (enabled ? '启用' : '禁用'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个菜单吗？"
            onConfirm={() => handleDeleteMenu(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#0d1117', color: '#e6edf3' }}>
      <Card 
        title="菜单管理" 
        bordered={false}
        style={{
          background: '#161b22',
          border: '1px solid #30363d',
          color: '#e6edf3'
        }}
        headStyle={{
          color: '#e6edf3',
          borderBottom: '1px solid #30363d'
        }}
      >
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              创建菜单
            </Button>
            <span style={{ marginLeft: 16 }}>共 {menus.length} 个菜单</span>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <div style={{ marginBottom: 8, color: '#666' }}>
              表格前3行: {menus.slice(0, 3).map(m => m.name).join(', ')}
            </div>
            <Table
              columns={menuColumns}
              dataSource={menus}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              style={{
                background: '#161b22',
                color: '#e6edf3'
              }}
              headerStyle={{
                background: '#21262d',
                color: '#8b949e',
                borderBottom: '1px solid #30363d'
              }}
              rowClassName={(record, index) => 
                index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
              }
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 32 }}>
          <Col span={24}>
            <Card 
              title="用户菜单分配" 
              size="small"
              style={{
                background: '#161b22',
                border: '1px solid #30363d',
                color: '#e6edf3'
              }}
              headStyle={{
                color: '#e6edf3',
                borderBottom: '1px solid #30363d'
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="选择用户"
                    value={selectedUser}
                    onChange={setSelectedUser}
                  >
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.username}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Card title="可分配菜单" size="small" bodyStyle={{ maxHeight: 400, overflow: 'auto' }}>
                    {availableMenus.map(menu => (
                      <div key={menu.id} style={{ marginBottom: 8 }}>
                        <Button
                          block
                          onClick={() => handleAssignMenu(menu.id)}
                          icon={<ArrowRightOutlined />}
                        >
                          {menu.name}
                        </Button>
                      </div>
                    ))}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="用户已有菜单" size="small" bodyStyle={{ maxHeight: 400, overflow: 'auto' }}>
                    {userMenus.map(menu => (
                      <div key={menu.id} style={{ marginBottom: 8 }}>
                        <Button
                          block
                          danger
                          onClick={() => handleRemoveMenu(menu.id)}
                        >
                          {menu.name}
                        </Button>
                      </div>
                    ))}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Card>

      <Modal
        title="创建菜单"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleCreateMenu} layout="vertical">
          <Form.Item
            name="name"
            label="菜单名称"
            rules={[{ required: true, message: '请输入菜单名称' }]}
          >
            <Input placeholder="请输入菜单名称" />
          </Form.Item>
          <Form.Item name="icon" label="图标">
            <Input placeholder="请输入图标名称" />
          </Form.Item>
          <Form.Item name="path" label="路由路径">
            <Input placeholder="请输入路由路径" />
          </Form.Item>
          <Form.Item name="file_path" label="文件路径">
            <TextArea placeholder="请输入文件路径" rows={3} />
          </Form.Item>
          <Form.Item
            name="menu_type"
            label="菜单类型"
            initialValue="ROUTE"
          >
            <Select>
              <Option value="ROUTE">路由</Option>
              <Option value="FILE">文件</Option>
            </Select>
          </Form.Item>
          <Form.Item name="parent_id" label="父菜单">
            <Select placeholder="请选择父菜单" allowClear>
              {menus.map(menu => (
                <Option key={menu.id} value={menu.id}>
                  {menu.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              创建
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑菜单"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        <Form form={editForm} onFinish={handleEditMenu} layout="vertical">
          <Form.Item
            name="name"
            label="菜单名称"
            rules={[{ required: true, message: '请输入菜单名称' }]}
          >
            <Input placeholder="请输入菜单名称" />
          </Form.Item>
          <Form.Item name="icon" label="图标">
            <Input placeholder="请输入图标名称" />
          </Form.Item>
          <Form.Item name="path" label="路由路径">
            <Input placeholder="请输入路由路径" />
          </Form.Item>
          <Form.Item name="file_path" label="文件路径">
            <TextArea placeholder="请输入文件路径" rows={3} />
          </Form.Item>
          <Form.Item name="menu_type" label="菜单类型">
            <Select>
              <Option value="ROUTE">路由</Option>
              <Option value="FILE">文件</Option>
            </Select>
          </Form.Item>
          <Form.Item name="parent_id" label="父菜单">
            <Select placeholder="请选择父菜单" allowClear>
              {menus.map(menu => (
                <Option key={menu.id} value={menu.id}>
                  {menu.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              更新
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuManagement;
